import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Activity } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/signup")({
  component: SignupPage,
  head: () => ({ meta: [{ title: "Sign up — InboxPulse" }] }),
});

const schema = z.object({
  displayName: z.string().trim().min(1, "Name darf nicht leer sein").max(100),
  email: z.string().trim().email("Ungültige E-Mail-Adresse").max(255),
  password: z.string().min(8, "Mindestens 8 Zeichen").max(128),
});

function SignupPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [user, loading, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ displayName, email, password });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Eingaben ungültig");
      return;
    }
    setBusy(true);
    try {
      const redirectTo =
        typeof window !== "undefined" ? `${window.location.origin}/login` : undefined;
      const { data, error } = await supabase.auth.signUp({
        email: parsed.data.email,
        password: parsed.data.password,
        options: {
          emailRedirectTo: redirectTo,
          data: { display_name: parsed.data.displayName },
        },
      });
      if (error) throw error;
      if (data.session) {
        toast.success("Account erstellt — du bist eingeloggt.");
        navigate({ to: "/dashboard" });
      } else {
        setSent(true);
        toast.success("Bestätigungs-E-Mail gesendet. Bitte Postfach prüfen.");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Registrierung fehlgeschlagen";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[radial-gradient(ellipse_at_top,_var(--primary)/0.15,_transparent_50%)]">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="size-9 rounded-lg bg-[image:var(--gradient-primary)] flex items-center justify-center shadow-[var(--shadow-glow)]">
            <Activity className="size-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-semibold tracking-tight">InboxPulse</span>
        </Link>
        <Card className="p-8 bg-card/80 backdrop-blur border-border">
          {sent ? (
            <>
              <h1 className="text-2xl font-semibold mb-1">Fast geschafft</h1>
              <p className="text-sm text-muted-foreground mb-6">
                Wir haben dir eine Bestätigungs-E-Mail an{" "}
                <span className="text-foreground font-medium">{email}</span> gesendet. Klicke auf
                den Link, um deinen Account zu aktivieren.
              </p>
              <Link to="/login" className="text-sm text-primary hover:underline">
                Zurück zum Login
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold mb-1">Account erstellen</h1>
              <p className="text-sm text-muted-foreground mb-6">
                E-Mail + Passwort — für den Login. Die Google-Test-Adressen werden separat unter
                "Seed Inboxes" hinterlegt.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Name</Label>
                  <Input
                    id="displayName"
                    type="text"
                    required
                    maxLength={100}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Dein Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    maxLength={255}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    maxLength={128}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mindestens 8 Zeichen"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[image:var(--gradient-primary)] hover:opacity-90 transition"
                >
                  {busy ? "Bitte warten..." : "Account erstellen"}
                </Button>
              </form>
              <p className="mt-4 text-xs text-muted-foreground text-center">
                Schon einen Account?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Einloggen
                </Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
